import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, Modal } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../i18n/errorMessages';
import { setGuildMemberRole, type GuildMembershipRole } from '../lib/api';
import { useAuthStore, useServerStore } from '../stores';

interface Props {
  guildId: string;
  userId: string;
  displayName: string;
  membershipRole: GuildMembershipRole;
  canManage: boolean;
  onDialogOpenChange?: (open: boolean) => void;
}

export default function ProjectMemberRoleControl({
  guildId,
  userId,
  displayName,
  membershipRole,
  canManage,
  onDialogOpenChange,
}: Props) {
  const { t, i18n } = useLingui();
  const currentUser = useAuthStore((state) => state.currentUser);
  const reconcileMembership = useServerStore((state) => state.reconcileMembership);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const generation = useRef(0);
  const submissionInFlight = useRef(false);
  const nextRole: GuildMembershipRole = membershipRole === 'admin' ? 'member' : 'admin';
  const actionLabel = nextRole === 'admin' ? t`Promote to Admin` : t`Demote to Member`;

  useEffect(() => {
    if (canManage) return;
    generation.current++;
    setOpen(false);
    setError(null);
  }, [canManage]);

  useEffect(() => {
    generation.current++;
    setOpen(false);
    setError(null);
  }, [guildId, membershipRole, userId]);

  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );

  if (!canManage) return null;

  const sameSession = (accountId: string | undefined) => {
    const auth = useAuthStore.getState();
    return auth.guildId === guildId && auth.currentUser?.id === accountId;
  };

  const submit = async () => {
    if (submitting || submissionInFlight.current) return;
    submissionInFlight.current = true;
    const request = ++generation.current;
    const accountId = currentUser?.id;
    setSubmitting(true);
    setError(null);
    try {
      await setGuildMemberRole(guildId, userId, nextRole);
      if (request !== generation.current || !sameSession(accountId)) return;
      setOpen(false);
      onDialogOpenChange?.(false);
      void reconcileMembership(guildId).catch(() => undefined);
    } catch (mutationError) {
      if (request !== generation.current || !sameSession(accountId)) return;
      setError(errorMessage(mutationError, msg`Could not change this Project role.`));
    } finally {
      submissionInFlight.current = false;
      if (request === generation.current) setSubmitting(false);
    }
  };

  const openConfirmation = () => {
    generation.current++;
    onDialogOpenChange?.(true);
    setError(null);
    setOpen(true);
  };

  return (
    <>
      <button type="button" role="menuitem" onClick={openConfirmation}>
        {actionLabel}
      </button>
      <Modal
        open={open}
        title={t`Change ${displayName} Project role`}
        okText={actionLabel}
        cancelText={t`Cancel`}
        okButtonProps={{ disabled: submitting }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        mask={{ closable: !submitting }}
        onOk={() => void submit()}
        onCancel={() => {
          generation.current++;
          setOpen(false);
          onDialogOpenChange?.(false);
        }}
        destroyOnHidden
      >
        <p>
          {nextRole === 'admin' ? (
            <Trans>Admins can manage ordinary Human and Agent memberships in this Project.</Trans>
          ) : (
            <Trans>This Human will keep Project access without Admin membership authority.</Trans>
          )}
        </p>
        {error && (
          <Alert type="error" showIcon message={i18n._(error)} role="alert" />
        )}
      </Modal>
    </>
  );
}
